import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { Observable, switchMap, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import { DeviceTokenService } from '../shared/services/device-token.service';
import { SessionService } from '../modules/auth/services/session.service';

/**
 * Dokłada nagłówki tożsamości urządzenia (`X-Device-Token` + `X-Device-Id`) do
 * anonimowych żądań Privacy Center / consents.
 *
 * Zasady:
 *  - Tylko nasze API (apiUrl) i tylko endpointy /consents oraz /privacy/* —
 *    nigdy do żądań cross-origin (te niestandardowe nagłówki wymuszają preflight CORS).
 *  - Wyłączamy sam /privacy/device-token, żeby nie wpaść w rekurencję.
 *  - Dla ZALOGOWANYCH użytkowników nic nie dokładamy: backend na trasach @Public
 *    robi best-effort i ustala tożsamość po JWT (Authorization: Bearer), więc nagłówki
 *    urządzenia są dla nich ignorowane — nie marnujemy wywołania device-token.
 */
@Injectable()
export class DeviceHeaderInterceptor implements HttpInterceptor {
  private readonly deviceToken = inject(DeviceTokenService);
  private readonly sessionService = inject(SessionService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldAttach(request.url)) {
      return next.handle(request);
    }

    // Zalogowany → identyfikacja po JWT, nagłówki urządzenia zbędne.
    if (this.sessionService.getAccessToken()) {
      return next.handle(request);
    }

    return this.deviceToken.ensureDeviceIdentity().pipe(
      switchMap(({ deviceId, deviceToken }) =>
        next.handle(
          request.clone({
            setHeaders: {
              'X-Device-Token': deviceToken,
              'X-Device-Id': deviceId,
            },
          }),
        ),
      ),
      // Gdy wydanie tokenu urządzenia padnie, wyślij żądanie bez nagłówków.
      // Backend w trybie przejściowym (DEVICE_TOKEN_REQUIRED != 'true') je akceptuje.
      catchError(() => next.handle(request)),
    );
  }

  private shouldAttach(url: string): boolean {
    if (!url.startsWith(environment.apiUrl)) {
      return false;
    }
    if (url.includes('/privacy/device-token')) {
      return false;
    }
    if (url.includes('/privacy/')) {
      return true;
    }
    // Publiczne katalogi zgód NIE potrzebują tożsamości urządzenia. Pomijamy je,
    // by nie wymuszać preflightu z X-Device-Token na anonimowej rejestracji
    // (GET /consents/types) — to chroni przed regresją CORS przy cross-origin.
    if (url.includes('/consents/types') || url.includes('/consents/by-context')) {
      return false;
    }
    return url.includes('/consents');
  }
}
